import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate'
import { ProjectError, parseProject } from '../model/migrate'
import { CURRENT_VERSION, type ProjectFile, type SceneDoc } from '../model/schema'
import { type AssetRecord, extensionFor, MAX_ASSET_BYTES } from './assets'

const MAX_ENTRY_BYTES = MAX_ASSET_BYTES + 1024 * 1024

export function referencedAssetIds(doc: SceneDoc): Set<string> {
  const ids = new Set<string>()
  for (const s of doc.sources) if (s.clip) ids.add(s.clip.assetId)
  return ids
}

/** Pack the scene and the audio assets it references into a portable, versioned ZIP. */
export function exportProject(doc: SceneDoc, assets: ReadonlyMap<string, AssetRecord>): Uint8Array {
  const files: Record<string, Uint8Array | [Uint8Array, { level: 0 }]> = {}
  const meta: ProjectFile['assets'] = []
  for (const id of referencedAssetIds(doc)) {
    const rec = assets.get(id)
    if (!rec) continue // clip missing locally; the reference stays and will show as "missing" on reopen
    const path = `assets/${id}.${extensionFor(rec.fileName, rec.mime)}`
    files[path] = [new Uint8Array(rec.bytes), { level: 0 }] // audio is already compressed
    meta.push({ id, fileName: rec.fileName, path, mime: rec.mime })
  }
  const project: ProjectFile = { format: 'megaspace-project', version: CURRENT_VERSION, scene: doc, assets: meta }
  files['project.json'] = strToU8(JSON.stringify(project, null, 2))
  return zipSync(files)
}

export interface ImportedProject {
  doc: SceneDoc
  assets: Map<string, AssetRecord>
  /** Asset ids referenced by the scene but absent from the archive. */
  missing: string[]
}

export function importProject(bytes: Uint8Array): ImportedProject {
  let entries: Record<string, Uint8Array>
  try {
    entries = unzipSync(bytes, { filter: (f) => f.originalSize <= MAX_ENTRY_BYTES })
  } catch {
    throw new ProjectError('Could not read the file as a ZIP archive.')
  }
  const json = entries['project.json']
  if (!json) throw new ProjectError('project.json is missing from the archive.')
  let raw: unknown
  try {
    raw = JSON.parse(strFromU8(json))
  } catch {
    throw new ProjectError('project.json is not valid JSON.')
  }
  const { project, scene } = parseProject(raw)
  const assets = new Map<string, AssetRecord>()
  for (const a of project.assets) {
    const data = entries[a.path]
    if (!data) continue
    const copy = new Uint8Array(data.byteLength)
    copy.set(data)
    assets.set(a.id, { fileName: a.fileName, mime: a.mime, bytes: copy.buffer })
  }
  const missing = [...referencedAssetIds(scene)].filter((id) => !assets.has(id))
  return { doc: scene, assets, missing }
}
